import type { DateRange, GlobalFilter } from '@/types';
import {
  AUDIENCE_REPORT_KEYS,
  showsAndDateFromGlobalFilters,
  type AudienceReportKey,
  type DownloadPeriod,
} from '../DownloadDrawer/downloadDrawerUtils';

export const AUDIENCE_REPORT_OPTIONS: { label: string; value: AudienceReportKey }[] = [
  { label: 'Uniques', value: 'uniques' },
  { label: 'Apps & websites', value: 'platforms' },
  { label: 'Location', value: 'location' },
  { label: 'Device', value: 'device' },
];

export interface AudienceDownloadSettings {
  shows: string[];
  dateRange: DateRange;
  period: DownloadPeriod;
  reports: AudienceReportKey[];
}

/**
 * Seeds drawer settings from the page filters. An empty `selectedShows` means all shows.
 * Every report starts selected, in {@link AUDIENCE_REPORT_KEYS} order.
 */
export const buildDefaultDownloadSettings = (
  globalFilters: GlobalFilter
): AudienceDownloadSettings => ({
  ...showsAndDateFromGlobalFilters(globalFilters),
  period: 'month',
  reports: [...AUDIENCE_REPORT_KEYS],
});

/** Ranges over a year can only be downloaded at the total period. */
export const getEffectiveSettings = (
  settings: AudienceDownloadSettings,
  isRangeRestricted: boolean
): AudienceDownloadSettings => (isRangeRestricted ? { ...settings, period: 'all' } : settings);

/**
 * Applies the checkbox group's value to the settings. The group only reports what it shows, so a
 * hidden Uniques tick is kept at the total period for when the period changes back.
 * `isTotalPeriod` is the shown period, which can differ from `previous.period` for ranges over a year.
 */
export const applyReportsChange = (
  previous: AudienceDownloadSettings,
  checkedReports: AudienceReportKey[],
  isTotalPeriod: boolean
): AudienceDownloadSettings => {
  const selected = new Set<AudienceReportKey>(checkedReports);
  if (isTotalPeriod && previous.reports.includes('uniques')) {
    selected.add('uniques');
  }
  return { ...previous, reports: AUDIENCE_REPORT_KEYS.filter((key) => selected.has(key)) };
};
